import React, { useState } from "react";
import {
  Grid,
  Paper,
  Button,
  Divider,
  Checkbox,
  TextField,
  FormControlLabel,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { TuneOutlined } from "@mui/icons-material";
import SubmitButton from "components/Button/SubmitButton";

import FormDatePicker from "components/FormFields/FormDatePicker";
import PhysicianFilter from "components/physicianFilter/PhysicianFilter";

import { getFormattedDate, subtractFromDate } from "utils/date";
import {
  BILLABLE_OPTIONS,
  CLINIC_ROLES,
  CURRENT_DATE,
  TRANSMISSION_TYPE_OPTIONS,
  UCH_CLINICS,
} from "static/constants";
import AccountFilter from "components/common/filters/account/AccountFilter";
import FormSelectField from "components/FormFields/FormSelect";
import dayjs from "dayjs";
import { Formik } from "formik";
import { archiveReportValidationSchema } from "schemas/archiveReportValidationSchema";
import { DATE_FORMAT } from "static/date";
import { useSelector } from "react-redux";
import { filterNonUCHAccounts } from "utils/account.util";

const baseText = {
  fontFamily: "Roboto",
  fontSize: "16px",
  lineHeight: 1,
  letterSpacing: 0,
};

const useStyles = makeStyles({
  title: { ...baseText, fontWeight: 500, marginBottom: "10px" },
  subtitle: { ...baseText, fontWeight: 400, marginBottom: "10px" },
  toggleButton: {
    ...baseText,
    fontWeight: 400,
    textTransform: "none",
    color: "#757575",
    borderColor: "#E0E0E0",
    padding: "8px 16px",
    "&.Mui-selected": {
      color: "#638215",
      backgroundColor: "#F1F6E3",
      "&:hover": { backgroundColor: "#F1F6E3" },
    },
    "&:hover": {
      color: "#638215",
      backgroundColor: "#F1F6E3",
    },
  },
});

const ArchiveReportFilter = ({
  filter,
  regexPattern,
  onSubmit,
  onInputChange,
  clearFilter,
}) => {
  const [activeShortcut, setActiveShortcut] = useState(null);
  const classes = useStyles();

  const { currentUser } = useSelector((state) => state.auth);
  const filteredAccounts = filterNonUCHAccounts(currentUser?.accounts);

  return (
    <Paper
      elevation={0}
      style={{ padding: "0px 15px 15px", marginBottom: "15px" }}
    >
      <Grid
        container
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
      >
        <Grid item>
          <p className={classes.title}>Filter reports</p>
          <p className={classes.subtitle}>
            Apply filters to find the reports you want to download.
          </p>
        </Grid>
        <Grid item>
          <ToggleButtonGroup
            value={activeShortcut}
            exclusive
            onChange={(_, value) => {
              if (value === null) return;
              setActiveShortcut(value);
              onInputChange(
                "reportStartDate",
                getFormattedDate(
                  subtractFromDate(
                    CURRENT_DATE,
                    1,
                    value === "year" ? "years" : "months"
                  )
                )
              );
            }}
            size="small"
          >
            <ToggleButton value="year" className={classes.toggleButton}>
              Last 1 Year
            </ToggleButton>
            <ToggleButton value="month" className={classes.toggleButton}>
              Last 1 Month
            </ToggleButton>
          </ToggleButtonGroup>
        </Grid>

        {CLINIC_ROLES.includes(currentUser?.role) &&
          filteredAccounts?.length === 1 &&
          UCH_CLINICS.includes(filteredAccounts?.[0]) && (
            <>
              <Divider
                orientation="vertical"
                variant="middle"
                style={{ minHeight: 20 }}
              />

              <FormControlLabel
                style={{ margin: 0 }}
                control={
                  <Checkbox
                    onChange={(e, c) => {
                      onInputChange("reportsOnEvoke", c);
                    }}
                    checked={filter.reportsOnEvoke}
                    name={"reportsOnEvoke"}
                    value={"reportsOnEvoke"}
                  />
                }
                label={"Reports on Evoke"}
              />
            </>
          )}
      </Grid>
      <Formik
        initialValues={{ ...filter }}
        validationSchema={archiveReportValidationSchema(filter)}
        onSubmit={() => onSubmit()}
        enableReinitialize
      >
        {({
          values,
          setFieldValue,
          errors,
          touched,
          handleSubmit,
          handleBlur,
          resetForm,
        }) => {
          return (
            <>
              <Grid container spacing={2} alignItems="flex-start">
                <Grid item xs={12} md={2}>
                  <FormDatePicker
                    label="Report Start Date"
                    name="reportStartDate"
                    value={
                      filter?.reportStartDate
                        ? dayjs(filter?.reportStartDate)
                        : null
                    }
                    maxDate={dayjs(CURRENT_DATE)}
                    format={DATE_FORMAT.DEFAULT}
                    required
                    onChange={(date) => {
                      const value = date ? getFormattedDate(date) : null;
                      onInputChange("reportStartDate", value);
                      setFieldValue("reportStartDate", value);
                    }}
                    hasError={
                      touched.reportStartDate && Boolean(errors.reportStartDate)
                    }
                    errorMessage={
                      touched.reportStartDate && errors.reportStartDate
                    }
                    onBlur={handleBlur}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormDatePicker
                    label="Report End Date"
                    name="reportEndDate"
                    value={
                      filter?.reportEndDate
                        ? dayjs(filter?.reportEndDate)
                        : null
                    }
                    maxDate={dayjs(CURRENT_DATE)}
                    required
                    format={DATE_FORMAT.DEFAULT}
                    onChange={(date) => {
                      const value = date ? getFormattedDate(date) : null;
                      onInputChange("reportEndDate", value);
                      setFieldValue("reportEndDate", value);
                    }}
                    hasError={
                      touched.reportEndDate && Boolean(errors.reportEndDate)
                    }
                    errorMessage={touched.reportEndDate && errors.reportEndDate}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormDatePicker
                    label="Processed Start Date"
                    value={
                      filter?.processedStartDate
                        ? dayjs(filter?.processedStartDate)
                        : null
                    }
                    format={DATE_FORMAT.DEFAULT}
                    maxDate={dayjs(CURRENT_DATE)}
                    onChange={(date) => {
                      const value = date ? getFormattedDate(date) : null;
                      onInputChange("processedStartDate", value);
                      setFieldValue("processedStartDate", value);
                    }}
                    hasError={
                      touched.processedStartDate &&
                      Boolean(errors.processedStartDate)
                    }
                    errorMessage={
                      touched.processedStartDate && errors.processedStartDate
                    }
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormDatePicker
                    label="Processed End Date"
                    value={
                      filter?.processedEndDate
                        ? dayjs(filter?.processedEndDate)
                        : null
                    }
                    format={DATE_FORMAT.DEFAULT}
                    maxDate={dayjs(CURRENT_DATE)}
                    onChange={(date) => {
                      const value = date ? getFormattedDate(date) : null;
                      onInputChange("processedEndDate", value);
                      setFieldValue("processedEndDate", value);
                    }}
                    hasError={
                      touched.processedEndDate &&
                      Boolean(errors.processedEndDate)
                    }
                    errorMessage={
                      touched.processedEndDate && errors.processedEndDate
                    }
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormDatePicker
                    label="Last Billing Start Date"
                    value={
                      filter?.lastBillingStartDate
                        ? dayjs(filter?.lastBillingStartDate)
                        : null
                    }
                    maxDate={dayjs(CURRENT_DATE)}
                    format={DATE_FORMAT.DEFAULT}
                    onChange={(date) => {
                      const value = date ? getFormattedDate(date) : null;
                      onInputChange("lastBillingStartDate", value);
                      setFieldValue("lastBillingStartDate", value);
                    }}
                    margin="none"
                    hasError={
                      touched.lastBillingStartDate &&
                      Boolean(errors.lastBillingStartDate)
                    }
                    errorMessage={
                      touched.lastBillingStartDate &&
                      errors.lastBillingStartDate
                    }
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormDatePicker
                    label="Last Billing End Date"
                    value={
                      filter?.lastBillingEndDate
                        ? dayjs(filter?.lastBillingEndDate)
                        : null
                    }
                    maxDate={dayjs(CURRENT_DATE)}
                    format={DATE_FORMAT.DEFAULT}
                    onChange={(date) => {
                      const value = date ? getFormattedDate(date) : null;
                      onInputChange("lastBillingEndDate", value);
                      setFieldValue("lastBillingEndDate", value);
                    }}
                    margin="none"
                    hasError={
                      touched.lastBillingEndDate &&
                      Boolean(errors.lastBillingEndDate)
                    }
                    errorMessage={
                      touched.lastBillingEndDate && errors.lastBillingEndDate
                    }
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2} alignItems="flex-start">
                <Grid item xs={12} md={2}>
                  <TextField
                    label="Patient Name"
                    value={filter.patient_name}
                    variant="outlined"
                    fullWidth
                    onChange={(event) => {
                      if (regexPattern.test(event.target.value)) {
                        onInputChange("patient_name", event.target.value);
                        setFieldValue("patient_name", event.target.value);
                      }
                    }}
                    margin="none"
                    sx={{
                      ".MuiInputBase-input": {
                        height: "100%",
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    label="Patient Serial Number"
                    value={filter.patient_serial_num}
                    variant="outlined"
                    fullWidth
                    margin="none"
                    onChange={(event) => {
                      onInputChange("patient_serial_num", event.target.value);
                      setFieldValue("patient_serial_num", event.target.value);
                    }}
                    sx={{
                      ".MuiInputBase-input": {
                        height: "100%",
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <PhysicianFilter
                    value={filter.physicians}
                    onChange={(event) => {
                      onInputChange("physicians", event.target.value);
                      setFieldValue("physicians", event.target.value);
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <AccountFilter
                    required={false}
                    value={filter.account}
                    onChange={(event) => {
                      onInputChange("account", event.target.value);
                      setFieldValue("account", event.target.value);
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormSelectField
                    margin="none"
                    label="Billable"
                    value={filter.billable}
                    options={BILLABLE_OPTIONS}
                    onChange={(value) => {
                      onInputChange("billable", value);
                      setFieldValue("billable", value);
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormSelectField
                    margin="none"
                    label="Report Type"
                    value={filter.transmissionType}
                    options={TRANSMISSION_TYPE_OPTIONS}
                    onChange={(value) => {
                      onInputChange("transmissionType", value);
                      setFieldValue("transmissionType", value);
                    }}
                  />
                </Grid>
              </Grid>
              <Grid
                container
                spacing={1}
                className="mt-2"
                alignContent="center"
                justifyContent="flex-end"
              >
                <Grid item>
                  <Button
                    color="secondary"
                    variant="outlined"
                    onClick={() => {
                      clearFilter();
                      resetForm();
                      setActiveShortcut(null);
                    }}
                  >
                    Clear Filter
                  </Button>
                </Grid>
                <Grid item>
                  <SubmitButton
                    startIcon={<TuneOutlined />}
                    buttonText="Apply Filter"
                    onClick={(e) => handleSubmit(e)}
                    size="large"
                  />
                </Grid>
              </Grid>
            </>
          );
        }}
      </Formik>
    </Paper>
  );
};

export default ArchiveReportFilter;
